import { useState } from 'react';
import { TextField, Alert } from '@mui/material';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import Modal from 'src/components/modal/Modal';
import useTriggerFamousShipmentFetch from 'src/api/temp/famous/useTriggerFamousShipmentFetch';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ImportFamousShipmentModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [famousShipmentId, setFamousShipmentId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const triggerFetch = useTriggerFamousShipmentFetch(() => {
    onSuccess();
    handleClose();
  });

  const handleClose = () => {
    setFamousShipmentId('');
    setError(null);
    onClose();
  };

  const handleSubmit = () => {
    if (!famousShipmentId.trim()) {
      setError('Please enter a Famous Shipment ID');
      return;
    }

    setError(null);
    triggerFetch.mutate({ famousShipmentId: famousShipmentId.trim() });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Modal
      open={open}
      title="Import Famous Shipment"
      closeable
      onCloseButton={{
        text: 'Cancel',
        onClick: handleClose,
        disabled: triggerFetch.isLoading,
      }}
      onConfirmButton={{
        text: triggerFetch.isLoading ? 'Importing...' : 'Import Shipment',
        onClick: handleSubmit,
        disabled: triggerFetch.isLoading || !famousShipmentId.trim(),
        color: 'info',
        variant: 'contained',
      }}
      maxWidth="sm"
    >
      <MDBox p={3}>
        <MDTypography variant="body2" color="text" mb={3}>
          Enter the Famous Shipment ID to import the shipment data for EDI
          processing.
        </MDTypography>

        <TextField
          fullWidth
          label="Famous Shipment ID"
          value={famousShipmentId}
          onChange={(e) => setFamousShipmentId(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={triggerFetch.isLoading}
          placeholder="e.g., SH12345"
          variant="outlined"
          size="medium"
          sx={{ mb: 2 }}
          autoFocus
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
      </MDBox>
    </Modal>
  );
};

export default ImportFamousShipmentModal;
